import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import "../../styles/index.css";

const Contacts = ({
  data,
  onEdit,
  onDelete,
  searchQuery = "",
  fieldConfigs = [],
}) => {
  const [page, setPage] = React.useState(0);
  const [sortCol, setSortCol] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("asc");
  const rowsPerPage = 10;

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    let result = q
      ? data.filter((r) =>
          [
            r.full_name,
            r.email,
            r.church_name,
            r.church_age,
            r.city,
            r.country,
          ].some((v) => v?.toLowerCase().includes(q))
        )
      : [...data];

    if (sortCol) {
      result.sort((a, b) => {
        const av = (a[sortCol] ?? "").toString().toLowerCase();
        const bv = (b[sortCol] ?? "").toString().toLowerCase();
        const num_a = parseFloat(av),
          num_b = parseFloat(bv);
        const cmp =
          !isNaN(num_a) && !isNaN(num_b) ? num_a - num_b : av.localeCompare(bv);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return result;
  }, [data, searchQuery, sortCol, sortDir]);

  React.useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handlePrev = () => setPage((p) => Math.max(p - 1, 0));
  const handleNext = () => {
    if ((page + 1) * rowsPerPage < filtered.length) setPage((p) => p + 1);
  };

  const SortIcon = ({ col }) => {
    const isActive = sortCol === col;
    const Icon = isActive
      ? sortDir === "asc"
        ? ArrowUpwardIcon
        : ArrowDownwardIcon
      : SwapVertIcon;
    return (
      <Icon
        onClick={() => handleSort(col)}
        sx={{
          fontSize: 12,
          cursor: "pointer",
          flexShrink: 0,
          color: isActive ? "#ef2d2d" : "#b0bac4",
        }}
      />
    );
  };

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalCols = 7 + fieldConfigs.length;

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 600,
    color: "#4a5568",
    whiteSpace: "nowrap",
    px: 1.5,
    py: 1.2,
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e2e8f0",
  };

  const bodyCellSx = {
    px: 1.5,
    py: 1,
    borderBottom: "1px solid #edf2f7",
    maxWidth: 120,
    overflow: "hidden",
  };

  const formatCustomValue = (val, fieldType) => {
    if (val === null || val === undefined || val === "") return "-";
    if (Array.isArray(val)) return val.length > 0 ? val.join(", ") : "-";
    if (fieldType === "checkbox")
      return val === true || val === "true" ? "Yes" : "No";
    return String(val);
  };

  const tableMinWidth = 520 + fieldConfigs.length * 300;

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ overflowX: "auto" }}
      >
        <Table
          size="small"
          sx={{ minWidth: tableMinWidth, tableLayout: "auto" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Name <SortIcon col="full_name" />
                </Box>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Email <SortIcon col="email" />
                </Box>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Church <SortIcon col="church_name" />
                </Box>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Church Age <SortIcon col="church_age" />
                </Box>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  City <SortIcon col="city" />
                </Box>
              </TableCell>
              <TableCell sx={headerCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Country <SortIcon col="country" />
                </Box>
              </TableCell>

              {/* Dynamic custom field headers */}
              {fieldConfigs.map((fc) => (
                <TableCell key={fc._id} sx={headerCellSx}>
                  {fc.field_label}
                </TableCell>
              ))}

              <TableCell sx={{ ...headerCellSx, width: 60 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                    {searchQuery?.trim()
                      ? "No matching results found"
                      : "No data available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row, index) => (
                <TableRow key={row._id || index} hover>
                  {/* Name */}
                  <TableCell sx={{ ...bodyCellSx, minWidth: 90 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#2d3748",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.full_name || "-"}
                      </Typography>
                      {row.is_admin && (
                        <Box
                          sx={{
                            backgroundColor: "#a7e0f1",
                            color: "#009CCC",
                            fontSize: "9px",
                            px: 0.5,
                            py: "1px",
                            borderRadius: "4px",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          ADMIN
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  {/* Email */}
                  <TableCell sx={{ ...bodyCellSx, minWidth: 110 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#4a5568",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.email}
                    </Typography>
                  </TableCell>

                  {/* Church */}
                  <TableCell sx={{ ...bodyCellSx, minWidth: 90 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#4a5568",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.church_name || "-"}
                    </Typography>
                  </TableCell>

                  {/* Church Age */}
                  <TableCell sx={{ ...bodyCellSx, minWidth: 40 }}>
                    <Typography sx={{ fontSize: 11, color: "#4a5568" }}>
                      {row.church_age || "-"}
                    </Typography>
                  </TableCell>

                  {/* City */}
                  <TableCell sx={{ ...bodyCellSx, minWidth: 70 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#4a5568",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.city || "-"}
                    </Typography>
                  </TableCell>

                  {/* Country */}
                  <TableCell sx={{ ...bodyCellSx, minWidth: 70 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#4a5568",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.country || "-"}
                    </Typography>
                  </TableCell>

                  {/* Dynamic custom field values */}
                  {fieldConfigs.map((fc) => (
                    <TableCell
                      key={fc._id}
                      sx={{ ...bodyCellSx, minWidth: 90 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "#4a5568",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {formatCustomValue(
                          row.custom_data?.[fc.field_key],
                          fc.field_type
                        )}
                      </Typography>
                    </TableCell>
                  ))}

                  {/* Actions */}
                  <TableCell
                    sx={{ ...bodyCellSx, width: 60, textAlign: "right" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => onEdit(row)}
                        sx={{ p: "3px" }}
                      >
                        <EditSquareIcon
                          sx={{ fontSize: 15, color: "#a0aec0" }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(row._id)}
                        sx={{ p: "3px" }}
                      >
                        <DeleteOutlinedIcon
                          sx={{ fontSize: 15, color: "#a0aec0" }}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1.5,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 11, color: "#a0aec0" }}>
          {filtered.length === 0
            ? "0 - 0 of 0"
            : `${page * rowsPerPage + 1} - ${Math.min(
                (page + 1) * rowsPerPage,
                filtered.length
              )} of ${filtered.length}`}
        </Typography>
        <Box>
          <IconButton size="small" onClick={handlePrev} disabled={page === 0}>
            <KeyboardArrowLeftIcon sx={{ color: "#8b95a1", fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext}
            disabled={(page + 1) * rowsPerPage >= filtered.length}
          >
            <KeyboardArrowRightIcon sx={{ color: "#8b95a1", fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default Contacts;
